import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  LinearProgress,
  Avatar,
  Chip,
  Divider,
  Badge,
  Stack,
  Tooltip,
  Button,
  useMediaQuery,
} from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  Home,
  Email,
  Phone,
  AttachMoney,
  Elevator,
  Stairs,
  Inventory,
  Schedule,
  ErrorOutline,
  LocalShipping,
  Star,
  AccessTime,
  Storage as StorageIcon,
  Directions,
  Shield,
} from "@mui/icons-material";
import {
  getCarrierMovesLimited,
  getCarrierMovesByDwelling,
  getCarrierMovesCount,
  getCarrierMovesCountByDwelling,
} from "../../redux/action/carrier";
import AddRoadIcon from "@mui/icons-material/AddRoad";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import DisplayInventory from "../../modals/DisplayInventory";
import PackingBoxCountModal from "../../modals/PackingBoxCountModal";

const CarrierStats = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [moveServices, setMoveServices] = useState<any[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [dwellingFilter, setDwellingFilter] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const dispatch = useDispatch();
  const [totalMovesCount, setTotalMovesCount] = useState(0);
  const [apartmentCount, setApartmentCount] = useState(0);
  const [homeCount, setHomeCount] = useState(0);
  const [storageCount, setStorageCount] = useState(0);
  const [upcomingMovesCount, setUpcomingMovesCount] = useState(0);
  const servicesPerPage = 5;
  const [upcomingFilter, setUpcomingFilter] = useState(false);
  const [allMoveServices, setAllMoveServices] = useState<any[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const extractBulkyItems = (move: any) => {
    if (move.inventory) {
      console.log(move.inventory, "inventory--");
    }
  };
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const carrierId = localStorage.getItem("CarrierId");
        if (!carrierId) return;
        const totalResponse = await dispatch<any>(
          getCarrierMovesCount(carrierId)
        );
        setTotalMovesCount(unwrapResult(totalResponse));
        const apartmentRes = await dispatch<any>(
          getCarrierMovesCountByDwelling({
            carrierId,
            dwellingType: "apartment",
          })
        );
        setApartmentCount(unwrapResult(apartmentRes));

        const homeRes = await dispatch<any>(
          getCarrierMovesCountByDwelling({
            carrierId,
            dwellingType: "home",
          })
        );
        setHomeCount(unwrapResult(homeRes));
        const storageRes = await dispatch<any>(
          getCarrierMovesCountByDwelling({
            carrierId,
            dwellingType: "storage",
          })
        );
        setStorageCount(unwrapResult(storageRes));
        const allMovesRes = await dispatch<any>(
          getCarrierMovesLimited({
            carrierId,
            limit: 6,
            offset: 0,
          })
        );

        const allMoves = unwrapResult(allMovesRes);
        setAllMoveServices(allMoves);
        setUpcomingMovesCount(
          allMoves.filter((s: any) => new Date(s.moving_date) > new Date())
            .length
        );
      } catch (err) {
        console.error("Failed to fetch counts:", err);
      }
    };

    fetchCounts();
  }, [dispatch]);

  const lastServiceRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreServices();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const loadMoreServices = async () => {
    try {
      setLoading(true);
      const carrierId = localStorage.getItem("CarrierId");
      if (!carrierId) throw new Error("Carrier ID not found");

      let response;
      if (dwellingFilter) {
        response = await dispatch<any>(
          getCarrierMovesByDwelling({
            carrierId,
            dwellingType: dwellingFilter.toLowerCase(),
            limit: servicesPerPage,
            offset: currentOffset,
          })
        );
      } else {
        response = await dispatch<any>(
          getCarrierMovesLimited({
            carrierId,
            limit: servicesPerPage,
            offset: currentOffset,
          })
        );
      }

      const result = unwrapResult(response);
      const filteredResult = upcomingFilter
        ? result.filter((s: any) => new Date(s.moving_date) > new Date())
        : result;

      if (filteredResult.length === 0) {
        setHasMore(false);
      } else {
        setMoveServices((prev) => [...prev, ...filteredResult]);
        setCurrentOffset((prev) => prev + servicesPerPage);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch move services");
    } finally {
      setLoading(false);
    }
  };
  const fetchServices = async (
    filter?: string | null,
    upcomingOnly?: boolean
  ) => {
    try {
      setLoading(true);
      setHasMore(true);
      setCurrentOffset(0);
      const carrierId = localStorage.getItem("CarrierId");
      if (!carrierId) throw new Error("Carrier ID not found");

      let response;
      if (filter) {
        response = await dispatch<any>(
          getCarrierMovesByDwelling({
            carrierId,
            dwellingType: filter.toLowerCase(),
            limit: servicesPerPage,
            offset: 0,
          })
        );
      } else {
        response = await dispatch<any>(
          getCarrierMovesLimited({
            carrierId,
            limit: servicesPerPage,
            offset: 0,
          })
        );
      }

      const result = unwrapResult(response);
      const filteredResult = upcomingOnly
        ? result.filter((s: any) => new Date(s.moving_date) > new Date())
        : result;

      setMoveServices(filteredResult);
      setDwellingFilter(filter || null);
      setCurrentOffset(servicesPerPage);
      setHasMore(result.length >= servicesPerPage);
    } catch (err: any) {
      setError(err.message || "Failed to fetch move services");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchServices();
  }, []);
  const calculateTotalRevenue = (services: any[]) => {
    return services.reduce((sum, s) => {
      const deposit = s.initial_deposit || 0;
      return sum + deposit;
    }, 0);
  };

  if (loading && moveServices.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <LinearProgress sx={{ width: "100%" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: theme.palette.error.main }}>
        <ErrorOutline sx={{ fontSize: 40 }} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (moveServices.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">No move services found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        sx={{
          mb: 4,
          fontWeight: "bold",
          color: theme.palette.primary.main,
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: "1px",
          position: "relative",
          "&:after": {
            content: '""',
            display: "block",
            width: "80px",
            height: "4px",
            background: theme.palette.secondary.main,
            margin: "10px auto 0",
            borderRadius: "2px",
          },
        }}
      >
        Move Services Statistics
      </Typography>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: "16px",
          }}
        >
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={totalMovesCount}
              label="Total Moves"
              icon={<LocalShipping />}
              onClick={() => fetchServices(null)}
              gradient="linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)"
            />
          </Box>
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={upcomingMovesCount}
              label="Upcoming Moves"
              icon={<CalendarToday />}
              gradient="linear-gradient(135deg, #ff9800 0%, #ff5722 100%)"
              onClick={() => {
                const newFilterValue = !upcomingFilter;
                setUpcomingFilter(newFilterValue);
                setDwellingFilter(null);
                fetchServices(null, newFilterValue);
              }}
              active={upcomingFilter}
            />
          </Box>
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={`$${calculateTotalRevenue(
                allMoveServices
              ).toLocaleString()}`}
              label="Total Revenue"
              icon={<AttachMoney />}
              gradient="linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)"
            />
          </Box>
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={apartmentCount}
              label="Apartments"
              icon={<Home />}
              gradient="linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)"
              onClick={() => fetchServices("Apartment")}
              active={dwellingFilter === "Apartment"}
              disabled={apartmentCount === 0}
            />
          </Box>
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={homeCount}
              label="Homes"
              icon={<Home />}
              gradient="linear-gradient(135deg, #ff5722 0%, #ff9800 100%)"
              onClick={() => fetchServices("Home")}
              active={dwellingFilter === "Home"}
              disabled={homeCount === 0}
            />
          </Box>
          <Box
            sx={{
              gridColumn: { xs: "1", sm: "span 1", md: "span 1" },
            }}
          >
            <StatCard
              value={storageCount}
              label="Storage"
              icon={<StorageIcon />}
              gradient="linear-gradient(135deg, #607d8b 0%, #795548 100%)"
              onClick={() => fetchServices("Storage")}
              active={dwellingFilter === "Storage"}
              disabled={storageCount === 0}
            />
          </Box>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {moveServices.map((service, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            lg={4}
            key={service.id}
            ref={index === moveServices.length - 1 ? lastServiceRef : null}
          >
            <MoveServiceCard
              service={service}
              theme={theme}
              allMoveServices={allMoveServices}
              isMobile={isMobile}
            />
          </Grid>
        ))}
      </Grid>

      {loading && moveServices.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <LinearProgress sx={{ width: "100%" }} />
        </Box>
      )}

      {!hasMore && moveServices.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 3 }}
        >
          No more moves to load
        </Typography>
      )}
    </Box>
  );
};

interface StatCardProps {
  value: string | number;
  label: string;
  icon: React.ReactElement;
  gradient: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}

const StatCard = ({
  value,
  label,
  icon,
  gradient,
  onClick,
  active = false,
  disabled = false,
}: StatCardProps) => (
  <Card
    sx={{
      background: gradient,
      color: "white",
      borderRadius: "12px",
      p: 3,
      height: "100%",
      boxShadow: active
        ? `0 0 0 2px white, 0 0 0 4px ${gradient.split(" ")[2]}`
        : "0 4px 12px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
      "&:hover": {
        transform: disabled ? "none" : "translateY(-3px)",
        boxShadow:
          onClick && !disabled
            ? active
              ? `0 0 0 2px white, 0 0 0 4px ${
                  gradient.split(" ")[2]
                }, 0 8px 16px rgba(0,0,0,0.15)`
              : "0 8px 16px rgba(0,0,0,0.15)"
            : "default",
        cursor: onClick && !disabled ? "pointer" : "default",
      },
    }}
    onClick={disabled ? undefined : onClick}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="subtitle1">{label}</Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: "rgba(255,255,255,0.2)",
          width: 48,
          height: 48,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiSvgIcon-root": {
            fontSize: 24,
            color: "white",
          },
        }}
      >
        {icon}
      </Box>
    </Box>
  </Card>
);

const MoveServiceCard = ({
  service,
  theme,
  allMoveServices,
  isMobile,
}: {
  service: any;
  theme: any;
  allMoveServices: any;
  isMobile: boolean;
}) => {
  const [openInventoryModal, setOpenInventoryModal] = useState(false);
  const [selectedMoveServiceId, setSelectedMoveServiceId] = useState<
    number | null
  >(null);
  const handleOpenInventoryModal =
    (moveServiceId: number) =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setSelectedMoveServiceId(moveServiceId);
      setOpenInventoryModal(true);
    };

  const handleCloseInventoryModal = () => {
    setOpenInventoryModal(false);
    setSelectedMoveServiceId(null);
  };
  const showInventoryButton = service.inventory;
  const [openBoxCountModal, setOpenBoxCountModal] = useState(false);
  const handleOpenBoxCountModal = () => {
    setOpenBoxCountModal(true);
  };

  const handleCloseBoxCountModal = () => {
    setOpenBoxCountModal(false);
    setSelectedMoveServiceId(null);
  };
  const showBoxCountButton = service.packing_boxes_count;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const movingDate = new Date(service.moving_date);
  movingDate.setHours(0, 0, 0, 0);

  const isToday = movingDate.getTime() === today.getTime();
  const isPast = movingDate < today;
  const isUpcoming = movingDate > today;

  let status = "";
  let color = "";

  if (service.status === "cancelled") {
    status = "CANCELLED";
    color = theme.palette.error.main;
  } else if (isToday) {
    status = "TODAY";
    color = theme.palette.success.main;
  } else if (isPast) {
    status = "PAST";
    color = theme.palette.grey[600];
  } else if (isUpcoming) {
    status = "UPCOMING";
    color = theme.palette.info.main;
  }
  const parseMoveItems = (itemsString: string): any[] => {
    try {
      const parsed = JSON.parse(itemsString);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => ({
          name: item.name || "",
          fee: Number(item.fee) || 0,
        }));
      }
      if (typeof parsed === "object" && parsed !== null) {
        return Object.entries(parsed).map(([name, fee]) => ({
          name,
          fee: Number(fee) || 0,
        }));
      }

      return [];
    } catch (e) {
      console.error("Error parsing items:", e);
      return [];
    }
  };
  const items = parseMoveItems(service.items || "[]");
  const renderDwellingSpecificContent = () => {
    switch (service.dwelling_type.toLowerCase()) {
      case "apartment":
        return (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {service.floor_number && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                    Floor Number:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                      Floor Number:
                    </Typography>
                    <Chip
                      label={service.floor_number}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {service.isElevator && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                    Accessibility:
                  </Typography>
                  <Chip
                    label={service.isElevator}
                    size="small"
                    icon={service.isElevator && <Elevator fontSize="small" />}
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    }}
                  />
                </>
              )}
              {service.isStairs && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                    Accessibility:
                  </Typography>
                  <Chip
                    label={service.isStairs}
                    size="small"
                    icon={service.isStairs && <Stairs fontSize="small" />}
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    }}
                  />
                </>
              )}
              {service.not_applicable && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                    Accessibility:
                  </Typography>
                  <Chip
                    label={service.not_applicable}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      color: theme.palette.text.primary,
                    }}
                  />
                </>
              )}
            </Box>
            {service.distance > 200 ? (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ mb: 1, color: theme.palette.text.secondary }}
                >
                  BULKY ITEMS
                </Typography>
                {service.inventory && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {JSON.parse(service.inventory.inventory).map(
                      (room: any) => {
                        return room.images.map((image: any) => {
                          return image.items.map((item: any, index: number) => {
                            if (item.bulky === true) {
                              return (
                                <Chip
                                  key={index}
                                  label={`${item.object_name}: $${item.price}`}
                                  size="small"
                                  icon={<Inventory fontSize="small" />}
                                  sx={{
                                    backgroundColor:
                                      theme.palette.primary.light,
                                    color: "white",
                                    mb: 1,
                                  }}
                                />
                              );
                            }
                            return null; // Return null if item.bulky is not true to prevent unnecessary rendering
                          });
                        });
                      }
                    )}
                  </Stack>
                )}
              </Box>
            ) : service.items ? (
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ mb: 1, color: theme.palette.text.secondary }}
                >
                  BULKY ITEMS
                </Typography>
                {JSON.parse(service.items).map((item: any, index: number) => {
                  return (
                    <Chip
                      key={index}
                      label={`${item.name}: $${item.fee}`}
                      size="small"
                      icon={<Inventory fontSize="small" />}
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: "white",
                        mb: 1,
                      }}
                    />
                  );
                })}
              </Box>
            ) : null}
          </>
        );

      case "home":
        return (
          <>
            {service.distance > 200 ? (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ mb: 1, color: theme.palette.text.secondary }}
                >
                  BULKY ITEMS
                </Typography>
                {service.inventory && (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {JSON.parse(service.inventory.inventory).map(
                      (room: any) => {
                        return room.images.map((image: any) => {
                          return image.items.map((item: any, index: number) => {
                            if (item.bulky === true) {
                              return (
                                <Chip
                                  key={index}
                                  label={`${item.object_name}: $${item.price}`}
                                  size="small"
                                  icon={<Inventory fontSize="small" />}
                                  sx={{
                                    backgroundColor:
                                      theme.palette.primary.light,
                                    color: "white",
                                    mb: 1,
                                  }}
                                />
                              );
                            }
                            return null; // Return null if item.bulky is not true to prevent unnecessary rendering
                          });
                        });
                      }
                    )}
                  </Stack>
                )}
              </Box>
            ) : service.items ? (
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ mb: 1, color: theme.palette.text.secondary }}
                >
                  BULKY ITEMS
                </Typography>
                {JSON.parse(service.items).map((item: any, index: number) => {
                  return (
                    <Chip
                      key={index}
                      label={`${item.name}: $${item.fee}`}
                      size="small"
                      icon={<Inventory fontSize="small" />}
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: "white",
                        mb: 1,
                      }}
                    />
                  );
                })}
              </Box>
            ) : null}

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                Home Details:
              </Typography>
              <Typography variant="body2">
                {service.number_of_bedrooms}
              </Typography>
            </Box>
          </>
        );

      case "storage":
        return (
          <>
            {service.storage && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <StorageIcon
                          sx={{
                            color: theme.palette.text.secondary,
                            mr: 1,
                            fontSize: "1rem",
                          }}
                        />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Size
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {service.storage.storage_size}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <StorageIcon
                          sx={{
                            color: theme.palette.text.secondary,
                            mr: 1,
                            fontSize: "1rem",
                          }}
                        />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Filled
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {service.storage.storage_unit}%
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        <StorageIcon
                          sx={{
                            color: theme.palette.text.secondary,
                            mr: 1,
                            fontSize: "1rem",
                          }}
                        />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Floor
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {service.storage.storage_floor_number}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{ flex: 1, display: "flex", alignItems: "center" }}
                      >
                        {service.storage.isElevator ? (
                          <Elevator
                            sx={{
                              color: theme.palette.text.secondary,
                              mr: 1,
                              fontSize: "1rem",
                            }}
                          />
                        ) : (
                          <Stairs
                            sx={{
                              color: theme.palette.text.secondary,
                              mr: 1,
                              fontSize: "1rem",
                            }}
                          />
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Access
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {service.storage.isElevator ? "Elevator" : "Stairs"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            {service.items
              ? JSON.parse(service.items).map((item: any, index: number) => {
                  return (
                    <Chip
                      key={index}
                      label={`${item.name}: $${item.fee}`}
                      size="small"
                      icon={<Inventory fontSize="small" />}
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: "white",
                        mb: 1,
                      }}
                    />
                  );
                })
              : null}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform:
            service.status === "cancelled" ? "none" : "translateY(-5px)",
          boxShadow:
            service.status === "cancelled"
              ? "0 8px 16px rgba(0,0,0,0.1)"
              : "0 12px 24px rgba(0,0,0,0.15)",
        },
        position: "relative",
        overflow: "visible",
        border: "none",
        width: "100%",
        maxWidth: { xs: "100%", sm: "none" },
        opacity: service.status === "cancelled" ? 0.8 : 1,
      }}
    >
      <Box
        sx={{
          p: isMobile ? 1.5 : 2,
          backgroundColor: theme.palette.primary.main,
          color: "white",
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography variant="h6" fontWeight="bold" noWrap>
          Move Order #: {service.move_order_number}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          p: 3,
          pb: 0,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "auto" },
            overflow: "hidden",
          }}
        >
          <Box sx={{ ml: 2, minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              noWrap
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              {service.user?.first_name} {service.user?.last_name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <Email fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {service.user?.email}
              </Box>
            </Typography>
          </Box>
        </Box>
        {status && (
          <Box
            sx={{
              position: "absolute",
              top: isMobile ? 8 : 16,
              right: -4,
              backgroundColor: color,
              color: "white",
              px: 2,
              py: 0.5,
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:before": {
                content: '""',
                position: "absolute",
                right: 0,
                bottom: -4,
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: `4px solid ${color}`,
              },
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {status}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: service.initial_deposit
              ? theme.palette.success.main
              : theme.palette.error.main,
            color: "white",
            borderRadius: "8px",
            px: 2,
            py: 1,
            textAlign: "center",
            minWidth: "80px",
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-end", sm: "center" },
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {service.initial_deposit ? "PAID" : "UNPAID"}
          </Typography>
        </Box>
      </Box>

      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          pt: isMobile ? 1 : 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: "8px",
          }}
        >
          <Tooltip title={service.moving_from} arrow>
            <Box sx={{ textAlign: "center", maxWidth: "120px" }}>
              <LocationOn color="primary" />
              <Typography variant="caption" display="block">
                From
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                noWrap
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {service.moving_from}
              </Typography>
            </Box>
          </Tooltip>
          <Box
            sx={{
              width: "40px",
              height: "2px",
              backgroundColor: theme.palette.grey[300],
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                top: "-3px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />
          <Tooltip title={service.moving_to} arrow>
            <Box sx={{ textAlign: "center", maxWidth: "120px" }}>
              <LocationOn color="secondary" />
              <Typography variant="caption" display="block">
                To
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                noWrap
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {service.moving_to}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            mb: 2,
          }}
        >
          <Box>
            <DetailItem
              icon={
                <CalendarToday sx={{ color: theme.palette.text.secondary }} />
              }
              label="Date"
              value={format(new Date(service.moving_date), "MMM dd, yyyy")}
            />
          </Box>
          <Box>
            <DetailItem
              icon={<Home sx={{ color: theme.palette.text.secondary }} />}
              label="Dwelling"
              value={service.dwelling_type}
            />
          </Box>
          <Box>
            <DetailItem
              icon={<Home sx={{ color: theme.palette.text.secondary }} />}
              label="Bedrooms"
              value={service.number_of_bedrooms}
            />
          </Box>
          <Box>
            <DetailItem
              icon={<Shield sx={{ color: theme.palette.text.secondary }} />}
              label="Insurance"
              value={`${service.isInsurance ? "Yes" : "No"}`}
            />
          </Box>
          <Box>
            <DetailItem
              icon={
                <AddRoadIcon sx={{ color: theme.palette.text.secondary }} />
              }
              label="Distance"
              value={`${Math.ceil(service.distance) + " miles" || "0"}`}
            />
          </Box>
          <Box>
            <DetailItem
              icon={<Directions sx={{ color: theme.palette.text.secondary }} />}
              label="Move Type"
              value={`${service.move_type || ""}`}
            />
          </Box>
          {service.pickup_square_footage && (
            <Box>
              <DetailItem
                icon={
                  <LocationOn sx={{ color: theme.palette.text.secondary }} />
                }
                label="Square Feet"
                value={`${service.pickup_square_footage || ""}`}
              />
            </Box>
          )}
          {service.duration_time && (
            <Box>
              <DetailItem
                icon={
                  <AccessTime sx={{ color: theme.palette.text.secondary }} />
                }
                label="Duration Time"
                value={`${service.duration_time || ""}`}
              />
            </Box>
          )}
          {service.packingPrice && (
            <Box>
              <DetailItem
                icon={
                  <AttachMoney sx={{ color: theme.palette.text.secondary }} />
                }
                label="Packing Price"
                value={`$${service.packingPrice || ""}`}
              />
            </Box>
          )}
          {service.price && (
            <Box>
              <DetailItem
                icon={
                  <AttachMoney sx={{ color: theme.palette.text.secondary }} />
                }
                label="Move Price"
                value={`$${service.price || ""}`}
              />
            </Box>
          )}
          {service && (
            <Box>
              <DetailItem
                icon={
                  <AttachMoney sx={{ color: theme.palette.text.secondary }} />
                }
                label="Initial Deposit"
                value={`${
                  service.initial_deposit
                    ? `$${service.initial_deposit}`
                    : "Not Paid"
                }`}
              />
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ mb: 1, color: theme.palette.text.secondary }}
          >
            {service.dwelling_type.toUpperCase()} DETAILS
          </Typography>
          {renderDwellingSpecificContent()}
        </Box>

        {/* {service.dwelling_type.toLowerCase() !== "storage" && service.items && (
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ mb: 1, color: theme.palette.text.secondary }}
            >
              ITEMS TO MOVE
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {items.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.name} (Fee: $${item.fee})`}
                  size="small"
                  icon={<Inventory fontSize="small" />}
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    color: "white",
                    mb: 1,
                  }}
                />
              ))}
            </Stack>
          </Box>
        )} */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          {showInventoryButton && (
            <Button
              variant="contained"
              onClick={handleOpenInventoryModal(service.id)}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                textAlign: "center",
                fontWeight: "bold",
                padding: "12px 16px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                transition: "all 0.3s ease",
                flex: showBoxCountButton ? 1 : 1,
                minWidth: 0,
              }}
            >
              View Inventory
            </Button>
          )}

          {showBoxCountButton ? (
            <Button
              variant="contained"
              onClick={handleOpenBoxCountModal}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                textAlign: "center",
                fontWeight: "bold",
                padding: "12px 16px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                transition: "all 0.3s ease",
                flex: showInventoryButton ? 1 : 1,
                minWidth: 0,
              }}
            >
              View Box Count
            </Button>
          ) : null}
        </Box>

        {showInventoryButton && (
          <DisplayInventory
            open={openInventoryModal}
            onClose={handleCloseInventoryModal}
            moveServiceId={selectedMoveServiceId}
            allMoveServices={allMoveServices}
          />
        )}

        {showBoxCountButton && (
          <PackingBoxCountModal
            open={openBoxCountModal}
            onClose={handleCloseBoxCountModal}
            packingBoxesCount={service.packing_boxes_count}
          />
        )}
      </CardContent>

      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.palette.grey[50],
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Phone
            fontSize="small"
            sx={{ mr: 1, color: theme.palette.text.secondary }}
          />
          <Typography variant="body2">+1{service?.phone_number}</Typography>
        </Box>
        <Box>
          <Chip
            label={`Total: ${
              service.packingPrice
                ? `$${Number(service.price) + service.packingPrice}`
                : !service.packingPrice && service.price
                ? `$${Number(service.price)}`
                : "Not calculated yet"
            }`}
            size="small"
            sx={{
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.contrastText,
            }}
          />
        </Box>
      </Box>
    </Card>
  );
};

const DetailItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <Box sx={{ mr: 1 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="500">
        {value}
      </Typography>
    </Box>
  </Box>
);

export default CarrierStats;
